// src/components/BookingFlow.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./BookingFlow.css";

const generateBookingId = () => `REV-${Math.floor(10000 + Math.random() * 90000)}`;
const getDeliveryEstimate = () => { const d = new Date(); d.setDate(d.getDate() + Math.floor(14 + Math.random() * 14)); return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); };
const slug = (str = "") => String(str).toLowerCase().trim().replace(/\s+/g, "-");
const formatPrice = (price) => `₹${price.toFixed(2)} Lakhs`;

const models = {
  mustang: { "Mustang GT": "mustang-gt.webp", "Mach 1": "mustang-mach1.avif", "Shelby GT500": "mustang-gt500.webp", "Mach-E": "mustang-mache.jpeg" },
  lamborghini: { "Aventador": "lamborghini-aventador.webp", "Huracán": "lamborghini-huracan.avif", "Revuelto": "lamborghini-revuelto.avif", "Urus": "lamborghini-urus.jpg" },
  "aston-martin": { "DBX": "aston-martin-dbx.avif", "DB11": "aston-martin-db11.jpg", "Vantage": "aston-martin-vantage.jpg", "DBS Superleggera": "aston-martin-dbs.webp" },
  ferrari: { "F8 Tributo": "ferrari-f8.jpg", "SF90 Stradale": "ferrari-sf90.avif", "812 Superfast": "ferrari-812.jpg", "Roma": "ferrari-roma.webp", "Portofino": "ferrari-portofino.avif" },
  audi: { "A3": "audi-a3.jpg", "A4": "audi-a4.jpg", "A6": "audi-a6.avif", "A8": "audi-a8.avif", "Q5": "audi-q5.avif", "Q7": "audi-q7.avif", "RS7": "audi-rs7.jpeg", "R8": "audi-r8.jpeg", "e-tron GT": "audi-etron.webp" },
  mercedes: { "A-Class": "mercedes-aclass.avif", "C-Class": "mercedes-cclass.jpg", "E-Class": "mercedes-eclass.jpg", "S-Class": "mercedes-sclass.avif", "G-Class": "mercedes-gclass.webp", "AMG GT": "mercedes-amggt.jpg" },
  bmw: { "3 Series": "bmw-3.jpeg", "5 Series": "bmw-5.jpg", "X5": "bmw-x5.avif", "i4": "bmw-i4.jpg", "iX": "bmw-ix.jpg", "M3": "bmw-m3.jpg", "M5": "bmw-m5.avif" },
  porsche: { "911": "porsche-911.avif", "Cayenne": "porsche-cayenne.avif", "Macan": "porsche-macan.jpg", "Panamera": "porsche-panamera.avif", "Taycan": "porsche-taycan.avif" }
};

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`revora-toast toast-${type}`}>
      <span className="toast-icon">{type === 'success' ? '✓' : type === 'error' ? '✕' : type === 'warning' ? '⚠' : 'ℹ'}</span>
      <span className="toast-message">{message}</span>
      <button className="toast-close" onClick={onClose}>×</button>
    </div>
  );
};

const generateInvoicePDF = (bookingId, bookingData, bookerDetails, deliveryDate) => {
  const pkgs = Object.keys(bookingData.packages || {}).filter(k => bookingData.packages[k]).join(", ") || "None";
  const content = `
═══════════════════════════════════════════════════════════
                        REVORA INVOICE
═══════════════════════════════════════════════════════════
Invoice Date: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
Booking ID: ${bookingId}
───────────────────────────────────────────────────────────
CUSTOMER INFORMATION
───────────────────────────────────────────────────────────
Name:           ${bookerDetails.fullName}
Email:          ${bookerDetails.email}
Phone:          ${bookerDetails.phone}
Delivery:       ${bookerDetails.address}, ${bookerDetails.city}, ${bookerDetails.state} - ${bookerDetails.pincode}
───────────────────────────────────────────────────────────
VEHICLE DETAILS
───────────────────────────────────────────────────────────
Brand:          ${bookingData.brand}
Model:          ${bookingData.model}
Variant:        ${bookingData.variant || "Standard"}
Exterior:       ${bookingData.exteriorColor || "Not specified"}
Interior:       ${bookingData.interiorColor || "Black"}, ${bookingData.upholstery || "Leather"}
Wheels:         ${bookingData.wheel || "Standard"}
Sunroof:        ${bookingData.sunroof || "None"}
Transmission:   ${bookingData.transmission || "Automatic"}
Fuel:           ${bookingData.fuel || "Petrol"}
Engine:         ${bookingData.engineOption || "Standard"}
Exhaust:        ${bookingData.exhaust || "Standard"}
Packages:       ${pkgs}
───────────────────────────────────────────────────────────
PRICING
───────────────────────────────────────────────────────────
Estimated Price: ${bookingData.estimatedPrice ? formatPrice(bookingData.estimatedPrice) : "Contact for quote"}
───────────────────────────────────────────────────────────
DELIVERY
───────────────────────────────────────────────────────────
Estimated:      ${deliveryDate}
Status:         Confirmed
═══════════════════════════════════════════════════════════
Thank you for choosing Revora!
Contact: revora.official01@gmail.com | +91 98154-16752
═══════════════════════════════════════════════════════════`;
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = `Revora_Invoice_${bookingId}.txt`;
  document.body.appendChild(link); link.click(); document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// BookingSummary Component
const BookingSummary = ({ customization, onConfirm, onBack, showToast }) => {
  const { brand, model, variant, exteriorColor, packages = {}, estimatedPrice } = customization;
  const carImage = models[slug(brand)]?.[model] || "default-car.jpg";
  const selectedPackages = Object.keys(packages).filter(k => packages[k]);

  return (
    <section className="booking-page">
      <div className="booking-container">
        <div className="step-indicator"><span className="step active">1. Summary</span><span className="step">2. Payment</span><span className="step">3. Details</span><span className="step">4. Confirmation</span></div>
        <h1 className="booking-title">Booking Summary</h1>
        <p className="booking-subtitle">Review your customization before payment</p>
        <div className="booking-grid">
          <div className="car-section">
            <img src={`/images/${carImage}`} alt={`${brand} ${model}`} className="booking-car-image" />
            <div className="car-details"><h2>{brand} {model}</h2><p className="variant-text">{variant || "Standard"}</p></div>
          </div>
          <div className="details-section">
            <h3>Customization Details</h3>
            <div className="detail-row"><span>Exterior Color:</span><strong>{exteriorColor || "Not selected"}</strong></div>
            <div className="detail-row"><span>Interior:</span><strong>{customization.interiorColor || "Black"}, {customization.upholstery || "Leather"}</strong></div>
            <div className="detail-row"><span>Wheels:</span><strong>{customization.wheel || "Standard"}</strong></div>
            <div className="detail-row"><span>Sunroof:</span><strong>{customization.sunroof || "None"}</strong></div>
            <div className="detail-row"><span>Transmission:</span><strong>{customization.transmission || "Automatic"}</strong></div>
            <div className="detail-row"><span>Fuel Type:</span><strong>{customization.fuel || "Petrol"}</strong></div>
            <div className="detail-row"><span>Engine:</span><strong>{customization.engineOption || "Standard"}</strong></div>
            <div className="detail-row"><span>Exhaust:</span><strong>{customization.exhaust || "Standard"}</strong></div>
            {selectedPackages.length > 0 && <div className="detail-row"><span>Packages:</span><strong>{selectedPackages.join(", ")}</strong></div>}
            <div className="price-summary">
              <div className="detail-row total-row"><span>Estimated Price:</span><strong className="price-highlight">{estimatedPrice ? formatPrice(estimatedPrice) : "Contact for quote"}</strong></div>
            </div>
          </div>
        </div>
        <div className="action-buttons"><button className="btn-back" onClick={onBack}>← Back to Customize</button><button className="btn-next" onClick={onConfirm}>Proceed to Payment →</button></div>
      </div>
    </section>
  );
};

// PaymentPage Component
const PaymentPage = ({ bookingData, onConfirm, onBack, showToast }) => {
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [cardDetails, setCardDetails] = useState({ number: "", name: "", expiry: "", cvv: "" });
  const [upiId, setUpiId] = useState("");

  const handleSubmit = () => {
    if (paymentMethod === "card" && (!cardDetails.number || !cardDetails.name || !cardDetails.expiry || !cardDetails.cvv)) {
      showToast("Please fill all payment details", "error"); return;
    }
    if (paymentMethod === "upi" && !upiId) { showToast("Please enter your UPI ID", "error"); return; }
    showToast("Payment verified successfully!", "success");
    setTimeout(onConfirm, 1000);
  };

  return (
    <section className="booking-page">
      <div className="booking-container">
        <div className="step-indicator"><span className="step done">1. Summary</span><span className="step active">2. Payment</span><span className="step">3. Details</span><span className="step">4. Confirmation</span></div>
        <h1 className="booking-title">Payment</h1>
        <p className="booking-subtitle">Secure payment for your {bookingData.brand} {bookingData.model}</p>
        <div className="payment-card">
          <div className="payment-amount"><span>Amount to Pay:</span><strong>{bookingData.estimatedPrice ? formatPrice(bookingData.estimatedPrice) : "Contact for quote"}</strong></div>
          <div className="payment-methods">
            <div className={`payment-method ${paymentMethod === "card" ? "active" : ""}`} onClick={() => setPaymentMethod("card")}><span className="icon">💳</span><span>Credit/Debit Card</span></div>
            <div className={`payment-method ${paymentMethod === "upi" ? "active" : ""}`} onClick={() => setPaymentMethod("upi")}><span className="icon">📱</span><span>UPI</span></div>
          </div>
          {paymentMethod === "card" && (
            <div className="payment-form">
              <div className="form-group"><label>Card Number</label><input type="text" placeholder="1234 5678 9012 3456" value={cardDetails.number} onChange={(e) => setCardDetails({...cardDetails, number: e.target.value})} maxLength="19" /></div>
              <div className="form-group"><label>Cardholder Name</label><input type="text" placeholder="John Doe" value={cardDetails.name} onChange={(e) => setCardDetails({...cardDetails, name: e.target.value})} /></div>
              <div className="form-row">
                <div className="form-group"><label>Expiry Date</label><input type="text" placeholder="MM/YY" value={cardDetails.expiry} onChange={(e) => setCardDetails({...cardDetails, expiry: e.target.value})} maxLength="5" /></div>
                <div className="form-group"><label>CVV</label><input type="text" placeholder="123" value={cardDetails.cvv} onChange={(e) => setCardDetails({...cardDetails, cvv: e.target.value})} maxLength="3" /></div>
              </div>
            </div>
          )}
          {paymentMethod === "upi" && (<div className="payment-form"><div className="form-group"><label>UPI ID</label><input type="text" placeholder="yourname@upi" value={upiId} onChange={(e) => setUpiId(e.target.value)} /></div></div>)}
          <div className="action-buttons"><button className="btn-back" onClick={onBack}>← Back</button><button className="btn-next" onClick={handleSubmit}>Pay & Continue →</button></div>
        </div>
      </div>
    </section>
  );
};

// BookerDetails Component
const BookerDetails = ({ onConfirm, onBack, showToast }) => {
  const [details, setDetails] = useState({ fullName: "", email: "", phone: "", address: "", city: "", state: "", pincode: "" });

  const handleSubmit = () => {
    if (!details.fullName || !details.email || !details.phone || !details.address || !details.city || !details.state || !details.pincode) {
      showToast("Please fill all required fields", "error"); return;
    }
    if (!/\S+@\S+\.\S+/.test(details.email)) { showToast("Please enter a valid email", "error"); return; }
    if (details.phone.length < 10) { showToast("Please enter a valid phone number", "error"); return; }
    onConfirm(details);
  };

  return (
    <section className="booking-page">
      <div className="booking-container">
        <div className="step-indicator"><span className="step done">1. Summary</span><span className="step done">2. Payment</span><span className="step active">3. Details</span><span className="step">4. Confirmation</span></div>
        <h1 className="booking-title">Delivery Details</h1>
        <p className="booking-subtitle">Where should we deliver your Revora?</p>
        <div className="details-card">
          <div className="form-group"><label>Full Name *</label><input type="text" value={details.fullName} onChange={(e) => setDetails({...details, fullName: e.target.value})} /></div>
          <div className="form-row">
            <div className="form-group"><label>Email *</label><input type="email" value={details.email} onChange={(e) => setDetails({...details, email: e.target.value})} /></div>
            <div className="form-group"><label>Phone *</label><input type="tel" value={details.phone} onChange={(e) => setDetails({...details, phone: e.target.value})} /></div>
          </div>
          <div className="form-group"><label>Delivery Address *</label><textarea value={details.address} onChange={(e) => setDetails({...details, address: e.target.value})} rows="3" /></div>
          <div className="form-row">
            <div className="form-group"><label>City *</label><input type="text" value={details.city} onChange={(e) => setDetails({...details, city: e.target.value})} /></div>
            <div className="form-group"><label>State *</label><input type="text" value={details.state} onChange={(e) => setDetails({...details, state: e.target.value})} /></div>
            <div className="form-group"><label>Pincode *</label><input type="text" value={details.pincode} onChange={(e) => setDetails({...details, pincode: e.target.value})} maxLength="6" /></div>
          </div>
          <div className="action-buttons"><button className="btn-back" onClick={onBack}>← Back</button><button className="btn-next" onClick={handleSubmit}>Confirm Booking →</button></div>
        </div>
      </div>
    </section>
  );
};

// SuccessPage Component
const SuccessPage = ({ bookingId, bookingData, bookerDetails, deliveryDate, showToast }) => {
  const navigate = useNavigate();
  const downloadInvoice = () => { generateInvoicePDF(bookingId, bookingData, bookerDetails, deliveryDate); showToast("Invoice downloaded successfully!", "success"); };

  return (
    <section className="booking-page success-page">
      <div className="success-container">
        <div className="success-icon">✓</div>
        <h1 className="success-title">Your {bookingData.brand} {bookingData.model} is Booked!</h1>
        <p className="success-subtitle">Congratulations on your new Revora</p>
        <div className="success-card">
          <div className="info-row"><span>Booking ID:</span><strong className="booking-id">{bookingId}</strong></div>
          <div className="info-row"><span>Vehicle:</span><strong>{bookingData.brand} {bookingData.model} {bookingData.variant}</strong></div>
          <div className="info-row"><span>Color:</span><strong>{bookingData.exteriorColor}</strong></div>
          <div className="info-row"><span>Estimated Price:</span><strong className="price-highlight">{bookingData.estimatedPrice ? formatPrice(bookingData.estimatedPrice) : "Contact for quote"}</strong></div>
          <div className="info-row"><span>Delivery:</span><strong>{deliveryDate}</strong></div>
          <div className="info-row"><span>Address:</span><strong>{bookerDetails.address}, {bookerDetails.city}</strong></div>
        </div>
        <div className="success-actions"><button className="btn-download" onClick={downloadInvoice}>📥 Download Invoice</button><button className="btn-home" onClick={() => navigate("/")}>🏠 Back to Home</button></div>
        <p className="success-note">You will receive a confirmation email shortly with all booking details.</p>
      </div>
    </section>
  );
};

// Main BookingFlow Component
export default function BookingFlow() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState("summary");
  const [bookingData, setBookingData] = useState(null);
  const [bookingId, setBookingId] = useState("");
  const [bookerDetails, setBookerDetails] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'info') => setToast({ message, type });

  useEffect(() => {
    const saved = localStorage.getItem("revoraCustomization");
    if (saved) { setBookingData(JSON.parse(saved)); }
    else { setBookingData({ brand: "Porsche", model: "911", variant: "Turbo S", exteriorColor: "Jet Black", interiorColor: "Black", upholstery: "Leather", wheel: '20" Forged', sunroof: "Panoramic", transmission: "Automatic", fuel: "Petrol", packages: { tech: true, sport: true }, estimatedPrice: 165 }); }
  }, []);

  const handleDetailsConfirm = (details) => {
    setBookerDetails(details);
    const id = generateBookingId();
    const delivery = getDeliveryEstimate();
    setBookingId(id); setDeliveryDate(delivery);
    const order = { id, type: "Vehicle Purchase", brand: bookingData.brand, model: bookingData.model, variant: bookingData.variant, customization: bookingData, bookerDetails: details, createdAt: new Date().toISOString(), status: "Confirmed", deliveryDate: delivery, estimatedPrice: bookingData.estimatedPrice };
    const orders = JSON.parse(localStorage.getItem("revoraOrders") || "[]");
    orders.unshift(order); localStorage.setItem("revoraOrders", JSON.stringify(orders));
    const activity = JSON.parse(localStorage.getItem("revoraActivity") || "[]");
    activity.unshift({ title: `${bookingData.brand} ${bookingData.model} - Booked`, description: `Booking ID: ${id} | Est: ${bookingData.estimatedPrice ? formatPrice(bookingData.estimatedPrice) : 'TBD'}`, status: "Confirmed", createdAt: new Date().toISOString() });
    localStorage.setItem("revoraActivity", JSON.stringify(activity));
    showToast("Booking confirmed successfully!", "success");
    setTimeout(() => setCurrentStep("success"), 500);
  };

  if (!bookingData) return <div className="loading-page"><div className="loader"></div><p>Loading your configuration...</p></div>;

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {currentStep === "summary" && <BookingSummary customization={bookingData} onConfirm={() => setCurrentStep("payment")} onBack={() => navigate("/customize")} showToast={showToast} />}
      {currentStep === "payment" && <PaymentPage bookingData={bookingData} onConfirm={() => setCurrentStep("details")} onBack={() => setCurrentStep("summary")} showToast={showToast} />}
      {currentStep === "details" && <BookerDetails onConfirm={handleDetailsConfirm} onBack={() => setCurrentStep("payment")} showToast={showToast} />}
      {currentStep === "success" && <SuccessPage bookingId={bookingId} bookingData={bookingData} bookerDetails={bookerDetails} deliveryDate={deliveryDate} showToast={showToast} />}
    </>
  );
}